import { useCallback, useMemo, useRef, useState } from 'react';
import { getAcceptedFileTypes } from '../../../constants/fileTypes';

/**
 * Handles file attachments: selection, concurrent upload, validation and removal.
 */
export function useFileAttachments({ isAgentChat, isImageUploadEnabled, uploadFileFn, setToastMessage }) {
    const [attachments, setAttachments] = useState([]);
    const fileInputRef = useRef(null);

    const isAnyFileUploading = useMemo(() => {
        return attachments.some(file =>
            file.progress !== undefined &&
            (file.progress < 100 || (typeof file.id === 'string' && file.id.startsWith('temp-')))
        );
    }, [attachments]);

    const handleFileSelect = useCallback(async e => {
        const files = Array.from(e.target.files);
        const uploadingFiles = files.map(file => ({
            id: 'temp-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9),
            name: file.name,
            type: file.type,
            size: file.size,
            progress: 0,
            file: file,
        }));

        setAttachments(prev => [...prev, ...uploadingFiles]);

        const uploadPromises = uploadingFiles.map(async uploadingFile => {
            try {
                const uploadedFile = await uploadFileFn(uploadingFile.file, progress => {
                    setAttachments(prev => prev.map(f =>
                        f.id === uploadingFile.id
                            ? { ...f, progress }
                            : f
                    ));
                });

                setAttachments(prev => prev.map(f =>
                    f.id === uploadingFile.id
                        ? {
                            ...f,
                            id: uploadedFile.id,
                            name: uploadedFile.name,
                            progress: 100,
                        }
                        : f
                ));
            } catch (error) {
                setAttachments(prev => prev.filter(f => f.id !== uploadingFile.id));
                setToastMessage && setToastMessage({
                    message: `${error.message || `Unable to upload ${uploadingFile.name}`}`,
                    type: 'error',
                });
            }
        });

        await Promise.all(uploadPromises);

        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, [uploadFileFn, setAttachments, setToastMessage]);

    const handleRemoveFile = useCallback(async fileId => {
        const attachment = attachments.find(f => f.id === fileId);
        if (attachment?.progress < 100 && attachment?.abortController) {
            attachment.abortController.abort();
        }
        setAttachments(prev => prev.filter(f => f.id !== fileId));
    }, [attachments]);

    const handleFileDrop = useCallback(files => {
        if (isAgentChat) {
            setToastMessage && setToastMessage({
                message: 'Agent chats do not support file uploads',
                type: 'error',
            });
            return;
        }

        const supportedExtensions = getAcceptedFileTypes(isImageUploadEnabled).split(',');

        const validFiles = files.filter(file => {
            const extension = `.${file.name.split('.').pop().toLowerCase()}`;
            const isImage = file.type.startsWith('image/');

            if (!isImageUploadEnabled && isImage) {
                return false;
            }

            return supportedExtensions.some(ext => ext.toLowerCase() === extension);
        });

        if (validFiles.length !== files.length) {
            setToastMessage && setToastMessage({
                message: isImageUploadEnabled
                    ? 'Some files are not supported and were skipped'
                    : 'Images are not supported with the current model',
                type: 'info',
            });
        }

        if (validFiles.length > 0) {
            handleFileSelect({ target: { files: validFiles } });
        }
    }, [handleFileSelect, isImageUploadEnabled, isAgentChat, setToastMessage]);

    const resetAttachments = useCallback(() => {
        setAttachments([]);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, []);

    return {
        attachments,
        setAttachments,
        isAnyFileUploading,
        handleFileSelect,
        handleRemoveFile,
        handleFileDrop,
        resetAttachments,
        fileInputRef,
    };
}
